import {onMounted, reactive, ref, watch} from 'vue';
import Debounce from './Debounce.js';

export function useDataTable(fetchRecords, orderColumnDefault = 'created_at', pageLengthDefault = 10, directionAscDefault = true, filterDefault = {}, localStorageSubKey = null, datatableRef = null) {
    const localStorageKey = 'TABLE-SETTINGS';
    const localStorageValue = localStorage.getItem(localStorageKey);
    const orderAsc = ref(directionAscDefault);
    const orderColumn = ref(orderColumnDefault);
    const pageLength = ref(pageLengthDefault);
    const search = ref('');
    const filter = ref(filterDefault);
    const page = ref(1);
    const totalRecords = ref(0);
    const isLoading = ref(false);
    const rows = ref([]);
    const initialSortBy = ref(null);

    // if we have table settings in LS, we set them
    if (localStorageValue && localStorageSubKey) {
        const localStorageSubValue = JSON.parse(localStorageValue)[localStorageSubKey];
        if (localStorageSubValue) {
            orderColumn.value = localStorageSubValue.orderColumn;
            orderAsc.value = localStorageSubValue.orderAsc;
            pageLength.value = localStorageSubValue.pageLength;
            search.value = localStorageSubValue.search;
            // výchozí filtr doplní klíče, které v uloženém chybí – opraví i dříve uložené nastavení,
            // ze kterého filtr sloupců vyřadil pevnou podmínku (např. resolved v Servisním centru)
            filter.value = {...filterDefault, ...(localStorageSubValue.filter ?? {})};
            page.value = localStorageSubValue.page;
            //datatableRef.setPage(page.value);
            setTimeout(() => {
                const datatable = datatableRef ? datatableRef.value : null;
                if (datatable) {
                    datatable.changePage(localStorageSubValue.page);
                    if (datatable.perPageChanged) {
                        datatable.perPageChanged({currentPerPage: localStorageSubValue.pageLength});
                    } else {
                        datatable.currentPerPage = localStorageSubValue.pageLength;
                    }
                    //datatable.sorts = [{field: localStorageSubValue.orderColumn, type: localStorageSubValue.orderAsc === true ? 'asc' : 'desc'}];
                    datatable.columnFilters = localStorageSubValue.filter;

                    datatable.columns.forEach(column => {
                        if (localStorageSubValue.filter[column.field]) {
                            column.filterOptions.filterValue = localStorageSubValue.filter[column.field];
                        }
                    })
                    //datatable.sortOptions = {enabled: true, initialSortBy: {field: localStorageSubValue.orderColumn, type: localStorageSubValue.orderAsc === true ? 'asc' : 'desc'}};
                    initialSortBy.value = {field: localStorageSubValue.orderColumn, type: localStorageSubValue.orderAsc === true ? 'asc' : 'desc'};
                }
            });
        }
    }

    const body = reactive({
        page: page,
        search: search,
        pageLength: pageLength,
        orderColumn: orderColumn,
        orderAsc: orderAsc,
        filter: filter
    });

    function onPageChange(params) {
        page.value = params.currentPage;
    }

    function onPerPageChange(params) {
        page.value = 1;
        pageLength.value = params.currentPerPage;
    }

    function onSortChange(params) {
        if (params.length > 0) {
            orderBy(params[0].field, params[0].type);
        }
    }

    function onColumnFilter(params) {
        page.value = 1;
        // tabulka posílá jen hodnoty sloupců – bez sloučení s výchozím filtrem by se ztratila pevná
        // podmínka, kterou sloupce nemají (např. resolved u záložek Servisního centra)
        filter.value = {...filterDefault, ...params.columnFilters};
    }

    function getReactiveValues() {
        return {
            search: search.value,
            pageLength: pageLength.value,
            page: page.value,
            orderColumn: orderColumn.value,
            orderAsc: orderAsc.value,
            filter: filter.value,
        };
    }

    function fetchWithValues() {
        isLoading.value = true;
        fetchRecords(getReactiveValues());
        saveToLocalStorage();
    }

    function saveToLocalStorage() {
        if (localStorageSubKey) {
            localStorage.setItem(localStorageKey, JSON.stringify({
                ...JSON.parse(localStorage.getItem(localStorageKey)),
                [localStorageSubKey]: getReactiveValues()
            }));
        }
    }

    onMounted(() => {
        fetchWithValues();
    });

    function orderBy(column, type) {
        orderColumn.value = column;
        orderAsc.value = type === 'asc';
        fetchWithValues();
    }

    watch(search, () => {
        isLoading.value = true;
        rows.value = [];
        Debounce(fetchWithValues, 500)();
    });
    watch(filter, () => {
        isLoading.value = true;
        rows.value = [];
        Debounce(fetchWithValues, 500)();
    }, {deep: true});
    watch(pageLength, fetchWithValues);
    watch(page, fetchWithValues);

    return {
        orderAsc,
        orderColumn,
        pageLength,
        search,
        filter,
        isLoading,
        rows,
        totalRecords,
        onColumnFilter,
        onPageChange,
        onPerPageChange,
        onSortChange,
        fetchWithValues,
        body,
        initialSortBy
    };
}
